import { faXmark } from '@fortawesome/free-solid-svg-icons';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';
import buttonStyles from '../styles/Button.module.css';
import styles from '../styles/Menu.module.css';
import { MenuGroup, MenuLink } from '../types';
import Icon from './Icon';
import InstallButton from './InstallButton';
import ThemeToggle from './ThemeToggle';

interface MenuProps {
  menuOpen: boolean;
  menu: MenuLink[];
  keyboardGroups: MenuGroup[];
  closeMenu: () => void;
}

const Menu = ({ menuOpen, menu, keyboardGroups, closeMenu }: MenuProps) => {
  const currentPath = usePathname();

  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (menuOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = 'visible';
    if (!menuOpen) return;
    // Move the focus into the menu, and let Escape close it
    closeButtonRef.current?.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMenu();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [menuOpen, closeMenu]);

  return (
    <>
      <div className={styles.fadeOut} data-open={menuOpen} onClick={() => closeMenu()}></div>
      {/* inert while closed, so its links can't be reached with Tab */}
      <nav id="site-menu" className={styles.menu} data-open={menuOpen} aria-label="Menu" inert={!menuOpen}>
        <div className={`${styles.links} ${styles.scrollbar}`}>
          {[{ heading: null, links: menu }, ...keyboardGroups].map(({ heading, links }, i) => (
            <section key={i} className={styles.group}>
              {heading && <h2 className={styles.heading}>{heading}</h2>}
              <ul>
                {links.map((item) => (
                  <li key={item.route} data-active={currentPath === item.route}>
                    <Link
                      href={item.route}
                      prefetch={false}
                      aria-current={currentPath === item.route ? 'page' : undefined}
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
        <button
          ref={closeButtonRef}
          onClick={() => closeMenu()}
          className={`${buttonStyles.menuButton} ${styles.closeButton}`}
          aria-label="Close menu"
        >
          <Icon icon={faXmark} size="2x" />
        </button>
        <div className={styles.menuText}>
          <ThemeToggle />
          <InstallButton />
          <div>
            Like this site?{' '}
            <a rel="noopener" target="_blank" href="https://www.buymeacoffee.com/willdrinkcoffee">
              Buy me a coffee
            </a>
            .
          </div>
        </div>
      </nav>
    </>
  );
};

export default Menu;
